import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAppContext } from "@/context/AppContext";
import { useNavigation } from "@/context/NavigationContext";
import AircraftProductCard from "@/components/AircraftProductCard";
import InstrumentLoader from "@/components/InstrumentLoader";
import {
  Search,
  Filter,
  Plane,
  LayoutGrid,
  Banknote,
  Activity,
  Zap,
  RotateCcw
} from "lucide-react";

interface AircraftCategoryPageProps {
  title: string;
  subcategories?: string[];
}

type SortOption = "price-asc" | "price-desc" | "date-new" | "date-old";

interface CategoryOption {
  code: string;
  label: string;
}

interface CategoryGroup {
  label: string;
  options: CategoryOption[];
}

interface StateOption {
  code: string;
  label: string;
}

const AircraftCategoryPage = ({ title }: AircraftCategoryPageProps) => {
  const { navigateTo } = useNavigation();
  const { getDictValue, getDictJson } = useAuth();

  // 🟢 Extract listings state, fetch function, and loader from AppContext
  const {
    aircraftListings,
    fetchAircraftListings,
    isAircraftListingsLoading
  } = useAppContext();

  const [search, setSearch] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState<SortOption>("date-new");
  const [flightTimeMax, setFlightTimeMax] = useState("");
  const [yearMin, setYearMin] = useState("");
  const [hpMin, setHpMin] = useState("");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // 🟢 Preluare dinamică a categoriilor folosind getDictJson
  const categoriesList = useMemo(() => {
    return getDictJson<CategoryGroup[]>("listing.aircraft.filter.categories", []);
  }, [getDictJson]);

  // 🟢 Preluare dinamică a stărilor aeronavei folosind getDictJson
  const aircraftStatesList = useMemo(() => {
    return getDictJson<StateOption[]>("listing.aircraft.filter.states", []);
  }, [getDictJson]);

  // 🟢 Fetch listings on component mount when the page opens
  useEffect(() => {
    fetchAircraftListings().catch((error) => {
      console.error("Failed to fetch aircraft listings on page load:", error);
    });
  }, [fetchAircraftListings]);

  const filtered = useMemo(() => {
    if (isAircraftListingsLoading) return [];

    let result = [...aircraftListings];

    // 1. FILTRU CĂUTARE TEXTUALĂ
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        l.model?.toLowerCase().includes(q) ||
        l.manufacturer?.toLowerCase().includes(q) ||
        (l as any).builder?.toLowerCase().includes(q) ||
        l.registration?.toLowerCase().includes(q)
      );
    }

    // 2. FILTRU SUBCATEGORII
    if (selectedSubcategories.length > 0) {
      result = result.filter(l => l.type && selectedSubcategories.includes(l.type));
    }

    // 3. FILTRU INTERVAL PREȚ
    if (priceMin) {
      result = result.filter(l => Number(l.grossValue || 0) >= Number(priceMin));
    }
    if (priceMax) {
      result = result.filter(l => Number(l.grossValue || 0) <= Number(priceMax));
    }

    // 4. FILTRU AN FABRICAȚIE
    if (yearMin) {
      result = result.filter(l =>
        l.productionYear && Number(l.productionYear) >= Number(yearMin)
      );
    }

    // 5. FILTRU ORE DE ZBOR
    if (flightTimeMax) {
      result = result.filter(l => Number(l.flightTime || 0) <= Number(flightTimeMax));
    }

    // 6. FILTRU PUTERE MOTOR
    if (hpMin) {
      result = result.filter(l => {
        const totalPower = (l as any).engines?.reduce((sum: number, eng: any) => sum + Number(eng.power || 0), 0) || 0;
        return totalPower >= Number(hpMin);
      });
    }

    // 7. FILTRU STARE/CONDIȚIE
    if (selectedStates.length > 0) {
      result = result.filter(l => l.state && selectedStates.includes(l.state));
    }

    // 8. SORTARE PREȚ / DATĂ
    switch (sort) {
      case "price-asc":
        result.sort((a, b) => {
          const valA = Number(a.grossValue);
          const valB = Number(b.grossValue);
          const pA = (!isNaN(valA) && valA > 0) ? valA : Number.MAX_VALUE;
          const pB = (!isNaN(valB) && valB > 0) ? valB : Number.MAX_VALUE;
          return pA - pB;
        });
        break;

      case "price-desc":
        result.sort((a, b) => {
          const valA = Number(a.grossValue);
          const valB = Number(b.grossValue);
          const pA = (!isNaN(valA) && valA > 0) ? valA : -1;
          const pB = (!isNaN(valB) && valB > 0) ? valB : -1;
          return pB - pA;
        });
        break;

      case "date-new":
        result.sort((a, b) =>
          new Date((b as any).registrationDate || b.productionYear || 0).getTime() -
          new Date((a as any).registrationDate || a.productionYear || 0).getTime()
        );
        break;

      case "date-old":
        result.sort((a, b) =>
          new Date((a as any).registrationDate || a.productionYear || 0).getTime() -
          new Date((b as any).registrationDate || b.productionYear || 0).getTime()
        );
        break;
    }

    return result;
  }, [
    isAircraftListingsLoading,
    aircraftListings,
    search,
    selectedSubcategories,
    priceMin,
    priceMax,
    yearMin,
    flightTimeMax,
    hpMin,
    selectedStates,
    sort
  ]);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={isAircraftListingsLoading} />

      <div className="container max-w-7xl py-12 px-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-orange-500 pl-6">
          <div>
            <p className="text-orange-500 font-mono text-sm tracking-[0.2em] mb-1 uppercase">
              {getDictValue("listing.aircraft.header") || "Marketplace // Aeronave de vânzare"}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight italic">
              {title || getDictValue("listing.aircraft.title")}
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4 border-r border-slate-800">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                {getDictValue("listing.aircraft.filter.match.txb") || "Unități găsite"}
              </p>
              <p className="text-orange-500 font-mono text-xl font-black">{filtered.length}</p>
            </div>
            <LayoutGrid className="text-slate-700 mr-2" size={20} />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start no-scrollbar">
          <aside className="lg:col-span-3 space-y-6 sticky top-24 h-[calc(100vh-120px)] overflow-y-auto no-scrollbar pb-10">
            <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl no-scrollbar">

              {/* HEADER FILTRE */}
              <div className="flex items-center justify-between mb-6 border-b border-slate-800/50 pb-4">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-orange-500" />
                  <h3 className="text-xs font-black uppercase tracking-widest text-white">
                    {getDictValue("listing.aircraft.filter.title") || "Filtre"}
                  </h3>
                </div>
                {(search || selectedSubcategories.length > 0 || priceMin || priceMax || yearMin || flightTimeMax || hpMin || selectedStates.length > 0) && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setSelectedSubcategories([]);
                      setPriceMin("");
                      setPriceMax("");
                      setYearMin("");
                      setFlightTimeMax("");
                      setHpMin("");
                      setSelectedStates([]);
                    }}
                    className="text-[10px] text-red-500 font-bold uppercase hover:text-red-400 flex items-center gap-1 transition-all active:scale-95"
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>

              <div className="space-y-8">

                {/* 1. IDENTIFICARE TEXTUALĂ */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.aircraft.filter.id.txb") || "Înmatriculare / Model"}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-orange-500 outline-none transition-all placeholder:text-slate-600"
                      placeholder={getDictValue("listing.aircraft.filter.id.placeholder") || "YR-ABC, Cessna..."}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>

                {/* 2. ORDONARE */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.aircraft.filter.sort.txb") || "Sortare"}
                  </label>
                  <div className="relative">
                    <select
                      value={sort}
                      onChange={(e) => setSort(e.target.value as SortOption)}
                      className="w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-orange-500 outline-none appearance-none cursor-pointer"
                    >
                      <option value="date-new">
                        {getDictValue("listing.aircraft.filter.sort.newest") || "Înmatriculare: Cele mai noi"}
                      </option>
                      <option value="date-old">
                        {getDictValue("listing.aircraft.filter.sort.oldest") || "Înmatriculare: Cele mai vechi"}
                      </option>
                      <option value="price-desc">
                        {getDictValue("listing.aircraft.filter.sort.costliest") || "Preț: Descrescător"}
                      </option>
                      <option value="price-asc">
                        {getDictValue("listing.aircraft.filter.sort.cheapest") || "Preț: Crescător"}
                      </option>
                    </select>
                    <LayoutGrid size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                  </div>
                </div>

                {/* 3. GRUPURI ȘI SUBCATEGORII */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 block ml-1">
                    {getDictValue("listing.aircraft.filter.category.txb") || "Categorie"}
                  </label>
                  <div className="space-y-2">
                    {categoriesList.map((category) => {
                      const isExpanded = expandedCategory === category.label;

                      return (
                        <div key={category.label} className="group">
                          <button
                            onClick={() => setExpandedCategory(isExpanded ? null : category.label)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                              isExpanded
                                ? "bg-slate-800 border-slate-600 text-white shadow-lg"
                                : "bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700"
                            }`}
                          >
                            <span className="text-[10px] font-black uppercase tracking-wider">{category.label}</span>
                            <Plane
                              size={12}
                              className={`transition-transform duration-300 ${isExpanded ? "rotate-90 text-orange-500" : "text-slate-600 group-hover:text-slate-400"}`}
                            />
                          </button>

                          {isExpanded && (
                            <div className="mt-2 grid grid-cols-1 gap-1.5 pl-2 animate-in fade-in slide-in-from-top-2 duration-300">
                              {category.options.map((opt) => {
                                const isSelected = selectedSubcategories.includes(opt.code);
                                return (
                                  <button
                                    key={opt.code}
                                    onClick={() => setSelectedSubcategories(prev =>
                                      isSelected ? prev.filter(c => c !== opt.code) : [...prev, opt.code]
                                    )}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[10px] font-bold uppercase border transition-all ${
                                      isSelected
                                        ? "bg-orange-600/20 border-orange-500 text-orange-400"
                                        : "bg-slate-950/40 border-slate-800 text-slate-500 hover:border-slate-700"
                                    }`}
                                  >
                                    <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.8)]" : "bg-slate-800"}`} />
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 4. INTERVAL PRET */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.aircraft.filter.budget.txb") || "Buget"}
                  </label>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <input
                      type="number"
                      placeholder="Min €"
                      className="h-10 px-3 rounded-lg bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-orange-500 transition-all"
                      value={priceMin}
                      onChange={(e) => setPriceMin(e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Max €"
                      className="h-10 px-3 rounded-lg bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-orange-500 transition-all"
                      value={priceMax}
                      onChange={(e) => setPriceMax(e.target.value)}
                    />
                  </div>
                  <div className="bg-blue-500/5 p-3 rounded-xl border border-blue-500/10 flex justify-between items-center">
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-blue-500 uppercase tracking-tighter">
                        {getDictValue("listing.aircraft.filter.currency.txb") || "Valută"}
                      </span>
                      <span className="text-lg font-black text-white italic">EURO <span className="text-blue-500">€</span></span>
                    </div>
                    <Banknote size={18} className="text-blue-500/50" />
                  </div>
                </div>

                {/* 5. SPECIFICATII TEHNICE (AN, ORE, HP) */}
                <div className="space-y-4 pt-4 border-t border-slate-800/50">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="relative">
                      <label className="text-[9px] font-bold text-slate-500 uppercase mb-1 block">
                        {getDictValue("listing.aircraft.filter.production.year.txb") || "An Fabricație De La"}
                      </label>
                      <input
                        type="number"
                        placeholder={getDictValue("listing.aircraft.filter.production.year.placeholder") || "Ex: 2005"}
                        className="w-full h-10 px-4 rounded-xl bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-orange-500"
                        value={yearMin}
                        onChange={(e) => setYearMin(e.target.value)}
                      />
                    </div>

                    <div className="relative">
                      <label className="text-[9px] font-bold text-slate-500 uppercase mb-1 block">
                        {getDictValue("listing.aircraft.filter.flight.time.txb") || "Ore Zbor (Max)"}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          placeholder={getDictValue("listing.aircraft.filter.flight.time.placeholder") || "Ex: 2000"}
                          className="w-full h-10 px-4 rounded-xl bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-orange-500"
                          value={flightTimeMax}
                          onChange={(e) => setFlightTimeMax(e.target.value)}
                        />
                        <Activity size={12} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600" />
                      </div>
                    </div>

                    <div className="relative">
                      <label className="text-[9px] font-bold text-slate-500 uppercase mb-1 block">
                        {getDictValue("listing.aircraft.filter.power.txb") || "Putere (Min Hp)"}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          placeholder={getDictValue("listing.aircraft.filter.power.placeholder") || "Ex: 150"}
                          className="w-full h-10 px-4 rounded-xl bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-orange-500"
                          value={hpMin}
                          onChange={(e) => setHpMin(e.target.value)}
                        />
                        <Zap size={12} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 6. STATUS / CONDITIE */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 block ml-1">
                    {getDictValue("listing.aircraft.filter.state.txb") || "Condiție generală"}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {aircraftStatesList.map((state) => {
                      const isSelected = selectedStates.includes(state.code);
                      return (
                        <button
                          key={state.code}
                          onClick={() => setSelectedStates(prev =>
                            isSelected
                              ? prev.filter(s => s !== state.code)
                              : [...prev, state.code]
                          )}
                          className={`py-2 rounded-lg text-[9px] font-bold uppercase border transition-all ${
                            isSelected
                              ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                              : "border-slate-800 bg-slate-900/40 text-slate-600 hover:border-slate-600"
                          }`}
                        >
                          {state.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* SUMAR REZULTATE */}
                <div className="pt-4 border-t border-slate-800/50">
                  <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800 flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${filtered.length > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <Activity size={16} className={filtered.length > 0 ? 'text-emerald-500' : 'text-red-500'} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase">
                        {getDictValue("listing.aircraft.filter.match.txb") || "Unități găsite"}
                      </p>
                      <p className="text-sm font-black text-white italic">
                        {filtered.length} {getDictValue("listing.aircraft.filter.match.results") || "Rezultate"}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </aside>

          <main className="lg:col-span-9 space-y-6">
            {isAircraftListingsLoading ? (
              <div className="flex items-center justify-center py-32">
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 bg-slate-900/10 rounded-[3rem] border border-dashed border-slate-800">
                <Plane size={32} className="text-slate-700 -rotate-45 mb-6" />
                <h4 className="text-white font-black uppercase tracking-widest mb-2">
                  {getDictValue("home.search.noResults") || "Nu s-au găsit anunțuri"}
                </h4>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                {filtered.map(listing => (
                  <div
                    key={listing.listingId || listing.registration}
                    onClick={() => navigateTo("aircraft-details", { listingId: listing.listingId })}
                    className="cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <AircraftProductCard listing={listing} />
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AircraftCategoryPage;